// The contract's `Project` over the Swift app's documents: ProjectDocument + FragmentDocument reads (documents.ts),
// `Arrangement.tree()`, `ManuscriptSelection`, `ProseMarkdown.wordCount(_:kind:)`, and from the app (Apps/Shared):
// `LibraryModel.title(of:)`, `displayValue`, `chapterOpeners` (Rooms/LibraryModel+Openers.swift), `loadChapterTexts`
// (LibraryModel+Statistics.swift), `subtreeBase` (…+Statistics) and `SearchIndex.dailyWords` over `dailyWords()`.
import type * as A from '@automerge/automerge';
import type {
  ArrangementInfo, Chapter, Checkpoint, Comment, DailyWords, Entity, EntityKind, Field, Fragment, FragmentKind, MetadataValue,
  OpenProject, Opener, Project, ProjectType, Targets, TreeNode,
} from './contract.ts';
import { currentRange as anchorRange } from './automerge/anchors.ts';
import { isFolderish, tree as buildTree, type ArrangementTreeNode } from './model/arrangement.ts';
import {
  readFragment, readProject, type Arrangement, type FragmentRecord, type Issue, type ProjectRecord, type Suggestion,
} from './model/documents.ts';
import { counted, scenes, type SelectionEntry } from './model/manuscriptSelection.ts';
import { numberWords } from './numberWords.ts';
import { isMusicalOf, projectKind } from './scriptFormat.ts';
import { isNumberedChapter, NUMBERED_FIELD } from './numbering.ts';
import { wordCount } from './proseMarkdown.ts';
import { fragmentTags, joinTags } from './tags.ts';
import { addingDaily, importedDaily, importedHistory, type ImportedDay } from './importedHistory.ts';
import { fieldDisplay } from './plan/fields.ts';

/** `StandardField`: well-known metadata field ids. */
export const StandardField = {
  status: 'status', label: 'label', pov: 'pov', wordTarget: 'word-target', sectionType: 'section-type', beat: 'beat',
  postNumber: 'post-number', publishDate: 'publish-date', tags: 'tags',
  storyTime: 'story-time', storyDuration: 'story-duration', setting: 'setting',
  /** A marker, not a declared field: `false` on a chapter or part that takes no number (`numbering.ts`). */
  numbered: NUMBERED_FIELD,
  /** A marker, not a declared field: `true` on a journal post New Post began with its own copy of the opening and
   *  sign-off (`postStart`), which compile fills in place instead of wrapping the post again. */
  ownPostMatter: 'own-post-matter',
} as const;

export { isNumberedChapter };

/** Which entities an entity-ref field names (the app's `laneOrder`/`FieldEditor`: POV → characters, setting → places). */
export function entityKindOf(fieldId: string): EntityKind | undefined {
  return fieldId === StandardField.pov ? 'character' : fieldId === StandardField.setting ? 'place' : undefined;
}

/** What `openProject` returns: the contract plus the reads behind it, for the app's other views. */
export interface OpenedProject extends Project {
  /** The project document as read. */
  readonly record: ProjectRecord;
  /** Fields that couldn't be read as the Swift expects (each read as its default). */
  issues(): Issue[];
  fragmentRecord(id: string): FragmentRecord | null;
  fragmentDoc(id: string): A.Doc<unknown> | null;
  /** Every fragment id with a document. */
  fragmentIds(): string[];
  suggestions(fragmentId: string): Suggestion[];
  synopsisComments(fragmentId: string): Comment[];
  /** Where a comment's passage is now (Unicode scalars of the current text), or null when it was cut. */
  currentRange(fragmentId: string, comment: Pick<Comment, 'anchorStart' | 'anchorEnd' | 'anchorHeads'>, on?: 'text' | 'synopsis'): [number, number] | null;
  /** The arrangement as `Arrangement.tree()` builds it (nodes as stored). */
  arrangementTree(arrangementId: string): ArrangementTreeNode[];
  /** `title(of:)` for a stored node. */
  nodeTitle(node: { fragmentID?: string; title?: string }): string;
  /** `daily`, over the given fragments (default: the counted ones, as Statistics shows) or `'all'`. The default and `'all'`
   *  add the days written in Scrivener before the project came here (`importedHistory`: the draft, or with `'all'` or
   *  count-all-text everything); a list of fragments is those fragments' logs only. */
  daily(fragmentIds?: Iterable<string> | 'all'): DailyWords[];
  /** The writing history imported from another app (`ImportedHistory`). */
  importedHistory(): ImportedDay[];
}

const FRAGMENT_PREFIX = 'fragment.';

function formatter<T>(make: (locale: string) => T, locale: string): T {
  try {
    return make(locale);
  } catch {
    return make('en');
  }
}

export const openProject: OpenProject = (docs) => open(docs);

/** What a later `open` can reuse of an earlier one: everything read from a document that is still the same object
 *  (Automerge documents are immutable values: a change makes a new one). */
interface Reads {
  projectDoc: A.Doc<unknown>;
  record: ProjectRecord;
  fragmentDocs: Map<string, A.Doc<unknown>>;
  records: Map<string, FragmentRecord | null>;
  words: Map<string, number>;
  trees: Map<string, ArrangementTreeNode[]>;
  counted: Map<boolean, string[]>;
  openers: () => Map<string, Opener> | null;
}
const reads = new WeakMap<object, Reads>();

/**
 * The project over these documents. With `previous` (an earlier `open` of the same project), whatever it read from a
 * document that hasn't changed since is reused — the project record, fragment records and word counts — so a rebuild
 * after typing in one scene reads that scene again, not the whole book.
 */
export function open(docs: Map<string, A.Doc<unknown>>, previous?: Project | null): OpenedProject {
  const projectDoc = docs.get('project');
  if (!projectDoc) throw new Error('No project document ("project") among the documents');
  const prev = previous ? reads.get(previous) : undefined;
  const sameProject = !!prev && prev.projectDoc === projectDoc;
  const record = sameProject ? prev!.record : readProject(projectDoc);
  const fragmentDocs = new Map<string, A.Doc<unknown>>();
  for (const [key, doc] of docs) if (key.startsWith(FRAGMENT_PREFIX)) fragmentDocs.set(key.slice(FRAGMENT_PREFIX.length), doc);

  const records = new Map<string, FragmentRecord | null>();
  const wordCache = new Map<string, number>();
  let fragmentsSame = !!prev && prev.fragmentDocs.size === fragmentDocs.size;
  if (prev) {
    for (const [id, doc] of fragmentDocs) {
      if (prev.fragmentDocs.get(id) !== doc) {
        fragmentsSame = false;
        continue;
      }
      if (prev.records.has(id)) records.set(id, prev.records.get(id)!);
      const n = prev.words.get(id);
      if (n !== undefined) wordCache.set(id, n);
    }
  }
  // Trees and the counted order depend on the project document alone; openers on it and the fragments' titles and fields.
  const trees = sameProject ? prev!.trees : new Map<string, ArrangementTreeNode[]>();
  const countedCache = sameProject ? prev!.counted : new Map<boolean, string[]>();
  const entries = new Map<string, SelectionEntry>([...record.fragments].map(([id, e]) => [id, { kind: e.kind, trashed: e.trashed }]));
  const manuscript: Arrangement | null = record.arrangements.find((a) => a.role === 'manuscript') ?? null;
  const tagFields = record.fields.filter((f) => f.type === 'tags').map((f) => f.id);
  const allFields = (): Field[] => record.fields.map((f) => {
    const kind = f.type === 'entity-ref' ? entityKindOf(f.id) : undefined;
    return { id: f.id, name: f.name, type: f.type, options: f.options.map((o) => ({ ...o })), ...(kind ? { entityKind: kind } : {}), ...(f.hidden ? { hidden: true as const } : {}) };
  });
  let openersCache: Map<string, Opener> | null = sameProject && fragmentsSame ? prev!.openers() : null;

  const fragmentRecord = (id: string): FragmentRecord | null => {
    if (!records.has(id)) {
      const doc = fragmentDocs.get(id);
      records.set(id, doc ? readFragment(doc) : null);
    }
    return records.get(id)!;
  };
  const arrangementTree = (id: string): ArrangementTreeNode[] => {
    let t = trees.get(id);
    if (!t) {
      const a = record.arrangements.find((x) => x.id === id);
      t = a ? buildTree(a) : [];
      trees.set(id, t);
    }
    return t;
  };
  const manuscriptTree = () => (manuscript ? arrangementTree(manuscript.id) : []);
  const nodeTitle = (node: { fragmentID?: string; title?: string }) =>
    node.fragmentID !== undefined ? (fragmentRecord(node.fragmentID)?.title ?? 'Untitled') : (node.title ?? 'Folder');
  const words = (id: string): number => {
    let n = wordCache.get(id);
    if (n === undefined) {
      const f = fragmentRecord(id);
      n = f ? wordCount(f.text, f.kind) : 0;
      wordCache.set(id, n);
    }
    return n;
  };
  const toTreeNode = (t: ArrangementTreeNode): TreeNode => ({
    id: t.node.id, fragmentId: t.node.fragmentID ?? null, title: nodeTitle(t.node), isFolder: t.node.isFolder,
    children: t.children.map(toTreeNode),
  });
  const countedIDs = (allText = false): string[] => {
    let ids = countedCache.get(allText);
    if (!ids) {
      ids = counted(manuscript, entries, allText, null, manuscript ? manuscriptTree() : undefined);
      countedCache.set(allText, ids);
    }
    return [...ids];
  };

  const numberFormat = formatter((l) => new Intl.NumberFormat(l, { maximumFractionDigits: 6 }), record.locale);
  const dateFormat = formatter((l) => new Intl.DateTimeFormat(l, { dateStyle: 'medium' }), record.locale);

  /** `LibraryModel.chapterOpeners`: parts and chapters numbered in reading order; manuscripts only. */
  // What a Section type makes of an item, as compile's map rules read it (`LibraryModel.openerKind`): Part, Chapter
  // and Heading decide (a heading is a chapter), Front Matter and Back Matter are neither, and any other type — a scene
  // converted to a folder keeps "Scene" — leaves it to the structure.
  const openerKind = (sectionType: string | undefined, structure: 'part' | 'chapter' | undefined): 'part' | 'chapter' | undefined => {
    switch (sectionType) {
      case 'part': return 'part';
      case 'chapter': case 'heading': return 'chapter';
      case 'front matter': case 'back matter': return undefined;
      default: return structure;
    }
  };
  const openers = (): Map<string, Opener> => {
    if (openersCache) return openersCache;
    const out = new Map<string, Opener>();
    openersCache = out;
    if (record.projectType !== 'manuscript' || !manuscript) return out;
    const field = record.fields.find((f) => f.id === StandardField.sectionType);
    const sectionType = (t: ArrangementTreeNode): string | undefined => {
      const id = t.node.fragmentID;
      const value = id !== undefined ? fragmentRecord(id)?.metadata[StandardField.sectionType] : undefined;
      if (value?.type !== 'text') return undefined;
      return (field?.options.find((o) => o.id === value.value)?.name ?? value.value).toLowerCase();
    };
    let parts = 0, chapters = 0;
    const walk = (nodes: ArrangementTreeNode[]) => {
      for (const t of nodes) {
        const kind = openerKind(sectionType(t), isFolderish(t) ? (t.children.some(isFolderish) ? 'part' : 'chapter') : undefined);
        const id = t.node.fragmentID;
        if (id !== undefined && (kind === 'part' || kind === 'chapter')) {
          // As compile numbers them: an unnumbered chapter (a prologue), or one left out of the compile, takes no
          // number and the count passes it by.
          const record = fragmentRecord(id);
          const numbered = isNumberedChapter(record?.metadata) && (record?.includeInCompile ?? true);
          let number: number | null = null;
          if (numbered) number = kind === 'part' ? ++parts : ++chapters;
          const label = number === null ? null : (kind === 'part' ? 'Part ' : 'Chapter ') + numberWords(number);
          out.set(id, { kind, number, label, title: nodeTitle(t.node) });
        }
        walk(t.children);
      }
    };
    walk(manuscriptTree());
    return out;
  };

  const daily = (fragmentIds?: Iterable<string> | 'all'): DailyWords[] => {
    const ids = fragmentIds === 'all' ? [...fragmentDocs.keys()] : [...new Set(fragmentIds ?? countedIDs(record.targets.countAllText))];
    const days = new Map<string, { added: number; removed: number }>();
    for (const id of ids) {
      const log = fragmentRecord(id)?.wordLog;
      if (!log) continue;
      for (const [key, amount] of Object.entries(log)) {
        // `key.split(separator: " ")`: empty pieces omitted; exactly day, device and sign.
        const parts = key.split(' ').filter((p) => p !== '');
        if (parts.length !== 3) continue;
        const day = days.get(parts[0]) ?? { added: 0, removed: 0 };
        if (parts[2] === '+') day.added += amount; else day.removed += amount;
        days.set(parts[0], day);
      }
    }
    const logged = [...days].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([day, w]) => ({ day, ...w }));
    if (fragmentIds !== undefined && fragmentIds !== 'all') return logged;
    const imported = importedDaily(importedHistory(record.settings), fragmentIds === 'all' || record.targets.countAllText);
    return imported.length ? addingDaily(logged, imported) : logged;
  };

  const project: OpenedProject = {
    id: record.id,
    title: record.title,
    type: record.projectType as ProjectType,
    scriptFormat: record.scriptFormat,
    kind: projectKind(record.projectType, record.scriptFormat),
    musical: isMusicalOf(record.projectType, record.settings),
    locale: record.locale,
    series: record.series,
    record,

    issues: () => [...record.issues, ...[...fragmentDocs.keys()].flatMap((id) => (fragmentRecord(id)?.issues ?? []).map((i) => ({ ...i, path: `fragment.${id}/${i.path}` })))],
    fragmentRecord,
    fragmentDoc: (id) => fragmentDocs.get(id) ?? null,
    fragmentIds: () => [...fragmentDocs.keys()],
    suggestions: (id) => fragmentRecord(id)?.suggestions ?? [],
    synopsisComments: (id) => fragmentRecord(id)?.synopsisComments ?? [],
    currentRange: (id, comment, on = 'text') => {
      const doc = fragmentDocs.get(id);
      return doc ? anchorRange(doc, comment, on) : null;
    },
    arrangementTree,
    nodeTitle,

    arrangements: (): ArrangementInfo[] => record.arrangements.map((a) => ({ id: a.id, name: a.name, role: a.role })),
    manuscriptId: () => manuscript?.id ?? null,
    tree: (arrangementId) => arrangementTree(arrangementId).map(toTreeNode),

    fragment: (id): Fragment | null => {
      const f = fragmentRecord(id);
      if (!f) return null;
      // A tags field reads as every tag the document has (two devices' lists, where they haven't been made one).
      let metadata = f.metadata;
      for (const field of tagFields) {
        if (!f.metadataConflicts[field]) continue;
        if (metadata === f.metadata) metadata = { ...f.metadata };
        metadata[field] = { type: 'text', value: joinTags(fragmentTags(f, field)) };
      }
      return {
        id, kind: f.kind, title: f.title, synopsis: f.synopsis, text: f.text, notes: f.notes, includeInCompile: f.includeInCompile,
        locked: f.locked, metadata, trashed: record.fragments.get(id)?.trashed ?? false, createdAt: f.createdAt, modifiedAt: f.modifiedAt,
        comments: f.comments,
        attachments: f.attachments,
      };
    },

    fields: (): Field[] => allFields().filter((f) => !f.hidden),
    allFields: (): Field[] => allFields(),

    displayValue: (fieldId, value?: MetadataValue) => {
      if (!value) return '';
      switch (value.type) {
        case 'text': {
          // A list field's option by name; one the writer took out reads as nothing, never as its id.
          const field = record.fields.find((f) => f.id === fieldId);
          if (field?.type === 'enum') return fieldDisplay(field, value);
          return record.entities.find((e) => e.id === value.value)?.name ?? value.value;
        }
        case 'number': return numberFormat.format(value.value);
        case 'boolean': return value.value ? 'Yes' : 'No';
        case 'date': return dateFormat.format(value.value);
      }
    },

    entities: (): Entity[] => record.entities.map((e) => ({ id: e.id, kind: e.kind, name: e.name, aliases: [...e.aliases], ...(e.color !== undefined ? { color: e.color } : {}) })),
    checkpoints: (): Checkpoint[] => record.checkpoints.map((c) => ({ id: c.id, name: c.name, trigger: c.trigger, createdAt: c.createdAt })),
    targets: (): Targets => ({
      manuscript: record.targets.manuscriptWords ?? null, session: record.targets.sessionWords ?? null, countAllText: record.targets.countAllText,
    }),

    counted: (allText = false) => countedIDs(allText),
    scenes: (kinds?: FragmentKind[]) => scenes(manuscript, entries, kinds ?? ['prose'], manuscript ? manuscriptTree() : undefined),
    words,
    manuscriptWords: () => countedIDs(record.targets.countAllText).reduce((n, id) => n + words(id), 0),
    subtreeWords: (node: TreeNode) => {
      // `subtreeBase`: every node's fragment (trash left out), the node's own included.
      const sum = (t: TreeNode): number => {
        const id = t.fragmentId;
        const own = id !== null && fragmentDocs.has(id) && !(record.fragments.get(id)?.trashed ?? false) ? words(id) : 0;
        return own + t.children.reduce((n, c) => n + sum(c), 0);
      };
      return sum(node);
    },

    chapters: (): Chapter[] => {
      // `loadChapterTexts`: the counted text grouped by chapter — a top-level item with children is a chapter unless
      // it holds folders, whose folders are then the chapters.
      const order = countedIDs(record.targets.countAllText).filter((id) => fragmentRecord(id) !== null);
      const position = new Map<string, number>();
      order.forEach((id, i) => { if (!position.has(id)) position.set(id, i); });
      const subtree = (t: ArrangementTreeNode): number[] => {
        const own = t.node.fragmentID !== undefined ? position.get(t.node.fragmentID) : undefined;
        return [...(own !== undefined ? [own] : []), ...t.children.flatMap(subtree)];
      };
      const all = openers();
      const out: Chapter[] = [];
      const add = (t: ArrangementTreeNode) => {
        const fragmentIds = subtree(t).map((i) => order[i]);
        out.push({
          nodeId: t.node.id, title: nodeTitle(t.node),
          opener: t.node.fragmentID !== undefined ? (all.get(t.node.fragmentID) ?? null) : null,
          fragmentIds, words: fragmentIds.reduce((n, id) => n + words(id), 0),
        });
      };
      for (const top of manuscriptTree()) {
        if (top.children.length === 0) continue;
        const chapters = top.children.filter((c) => c.children.length > 0);
        if (chapters.length === 0) add(top);
        chapters.forEach(add);
      }
      return out;
    },
    openers,
    daily,
    importedHistory: () => importedHistory(record.settings),
  };
  reads.set(project, { projectDoc, record, fragmentDocs, records, words: wordCache, trees, counted: countedCache, openers: () => openersCache });
  return project;
}
